import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState
} from "react";
import { useParams } from "react-router-dom";
import { useSession } from "../../Session";
import { getLive } from "../../api/live";
import { getRdcData } from "../../api/report";
import { transformComparisonFields } from "../../utils/comparison-fields";
import { transformListing } from "../../utils/listing";
import { allAreAcreage } from "../../utils/listings";
import { transformSubjectProperty } from "../../utils/subject-property";
import useModules from "./modules";

const LiveContext = createContext();

function LiveProvider({ children }) {
  const { currentUser } = useSession();
  const { guid } = useParams();
  const [agent, setAgent] = useState(window.agent || {});
  const [report, setReport] = useState({});
  const [isAcreageReport, setIsAcreageReport] = useState(false);
  const isReportOwnedByCurrentAgent = agent.guid === currentUser.guid;
  const [theme] = useState(window.theme || {});
  const [subjectProperty, setSubjectProperty] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [animationLoaded, setAnimationLoaded] = useState(false);
  const [appLoaded, setAppLoaded] = useState(false);
  const [appError, setAppError] = useState("");
  const sideBySideScrollPositionRef = useRef(0);
  const [comparisonFields, setComparisonFields] = useState([]);
  const [comparisonFieldsDefault, setComparisonFieldsDefault] = useState([]);
  const {
    modules,
    visibleModules,
    navigation,
    createModules,
    reorderModules,
    reorderSubModules,
    getModuleData,
    getModuleConfig,
    toggleModule,
    toggleSubModule,
    visibleListings,
    visibleListingsWithGhostListings,
    visibleListingGroups,
    listingsWithForgeListings,
    forgeListings,
    setForgeListings,
    toggleListing,
    isAddingListing,
    ghostListings,
    clearGhostListings,
    zestimateRequest,
    getSchools,
    addListingAdjustment,
    updateListingAdjustment,
    removeListingAdjustment,
    myRecentSales,
    setMyRecentSales
  } = useModules({ reportId: report.id, agent });
  const landMeasurement = (window.agent || {}).landMeasurement;
  const showAcresInsteadOfLotSize = landMeasurement === "acres";
  const [rdc, setRdc] = useState({
    data: undefined,
    loading: !!agent.hasRdcToolkit,
    success: !agent.hasRdcToolkit,
    error: undefined
  });

  useEffect(() => {
    if (!report.id || !agent.hasRdcToolkit) return;

    setRdc({
      data: undefined,
      loading: true,
      success: false,
      error: undefined
    });
    getRdcData(report.id)
      .then((data) =>
        setRdc({ data, loading: false, success: true, error: undefined })
      )
      .catch((error) =>
        setRdc({ data: null, loading: false, success: false, error })
      );
  }, [report.id, agent.hasRdcToolkit]);

  useEffect(() => {
    getLive(guid)
      .then((data) => {
        const report = data.report;
        const subjectProperty = transformSubjectProperty(data.subjectProperty);
        const listings = (data.listings || []).map((listing) =>
          transformListing(listing, (window.agent || {}).photoProxy)
        );
        const pages = data.pages || [];
        const links = report.links || {};
        const disallowedPages = data.disallowedPages || [];
        const isAcreageReport = allAreAcreage(listings);

        setMyRecentSales(
          (data.myRecentSales || []).map((listing) =>
            transformListing(
              { ...listing, isMyRecentSalesListing: true },
              (window.agent || {}).photoProxy
            )
          )
        );
        setReport(report);
        setAgent((agent) => ({ ...agent, ...data.agent }));

        if (subjectProperty != undefined) setSubjectProperty(subjectProperty);

        setIsAcreageReport(isAcreageReport);
        const comparisonFields = transformComparisonFields(
          data.comparisonFields
        );
        setComparisonFields(comparisonFields);
        setComparisonFieldsDefault(comparisonFields);
        createModules({
          listings,
          pages,
          links,
          disallowedPages,
          isAcreageReport,
          isWebLead: report.isWebLead
        });
        setDataLoaded(true);
      })
      .catch((error) => {
        // TODO: Report to airbrake?
        console.error(error);
        setAppError(error.message);
      });
  }, [guid, createModules]);

  useEffect(() => {
    if (dataLoaded && animationLoaded) {
      setAppLoaded(true);
    }
  }, [dataLoaded, animationLoaded]);

  return (
    <LiveContext.Provider
      value={{
        report,
        isAcreageReport,
        theme,
        agent,
        isReportOwnedByCurrentAgent,
        visibleListings,
        visibleListingsWithGhostListings,
        visibleListingGroups,
        listingsWithForgeListings,
        forgeListings,
        setForgeListings,
        toggleListing,
        isAddingListing,
        showAcresInsteadOfLotSize,
        ghostListings,
        clearGhostListings,
        zestimateRequest,
        rdc,
        getSchools,
        addListingAdjustment,
        updateListingAdjustment,
        removeListingAdjustment,
        subjectProperty,
        setSubjectProperty,
        isDrawing,
        setIsDrawing,
        modules,
        visibleModules,
        reorderModules,
        reorderSubModules,
        getModuleData,
        getModuleConfig,
        toggleModule,
        toggleSubModule,
        navigation,
        appLoaded,
        appError,
        setAnimationLoaded,
        sideBySideScrollPositionRef,
        comparisonFields,
        setComparisonFields,
        comparisonFieldsDefault,
        myRecentSales
      }}>
      {children}
    </LiveContext.Provider>
  );
}

function useLive() {
  const context = useContext(LiveContext);
  if (!context) {
    throw new Error(`useLive() must be used inside of a <LiveProvider />`);
  }
  return context;
}

export { LiveProvider, useLive };
